import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextLink from 'components/atoms/text-link'
import { formatDataTestE2eAttr } from 'utilities/format'
import Typography from './typography'

const StyledFormControl = styled(FormControl)`
  width: 100%;

  .MuiFilledInput-root {
    background-color: ${({ theme }) =>
      theme.colors.inputBackground || theme.colors.fieldBackground};
  }
  .MuiFilledInput-root.Mui-error {
    background-color: ${({ theme }) => theme.colors.errorInputBackground};
  }

  /* For outlined-input themes: when the label floats ABOVE the field (standard
     variant in shrunk state), give it a small left padding so it sits 10px
     from the input edge. Excludes filled/outlined variants where the label
     sits inside the field. Also tightens the dropdown's own horizontal padding
     to match (10px on each side). Filled variant uses .MuiFilledInput-root
     and stays untouched. */
  ${({ theme }) =>
    theme.colors.inputBorder &&
    `
    .MuiInputLabel-root.MuiInputLabel-shrink:not(.MuiInputLabel-filled):not(.MuiInputLabel-outlined) {
      padding-left: ${theme.sizeByFactor(1.25)};
    }
    .MuiInput-root {
      padding: 0 ${theme.sizeByFactor(1.25)};
    }
    /* Remove MUI's default 'top: calc(50% - 12px)' on the dropdown arrow for
       standard variant only — keeps filled/outlined dropdowns untouched. */
    .MuiInput-root .MuiSelect-icon {
      top: auto;
    }
    `}

  ${({ error, required, theme, $value }) => {
    if (!required || error || ![null, ''].includes($value)) {
      return ''
    }
    const requiredBg =
      theme.colors.inputBackground || theme.colors.requiredInputBackground
    const requiredAccent =
      theme.colors.inputBorderFocus || theme.colors.actionsCta
    return `
      .MuiInput-underline:before,
      .MuiInput-underline:after,
      .MuiInput-underline:hover:not(.Mui-disabled):before,
      .MuiFilledInput-underline:before,
      .MuiFilledInput-underline:after,
      .MuiFilledInput-underline:hover:not(.Mui-disabled):before {
        border-color: ${requiredAccent};
      }
      ${
        theme.colors.inputBorder
          ? `.MuiInput-root,
             .MuiFilledInput-root {
               outline-color: ${requiredAccent};
             }`
          : ''
      }
      .MuiFilledInput-root {
        background-color: ${requiredBg};
      }
    `
  }}
`

const Error = styled(Typography)`
  display: block;
  margin-left: 12px;
  margin-top: ${({ theme }) => theme.sizings.lvl0};
`

const StyledTextLink = styled(TextLink)`
  display: block;
  text-align: right;
  margin-top: ${({ theme }) => theme.sizings.lvl1};
`

/**
 * Dropdown select component which makes use of
 * [select input of Material-UI](https://material-ui.com/components/selects/).
 *
 * Whenever a selected item is passed that is not part of the items array it
 * should be added as an option.
 */

function NewDropdownSelect({
  className,
  disabled,
  error,
  filled,
  id,
  items,
  onChange,
  onBlur,
  label,
  required,
  value,
  selectionRequired,
  optinRequired,
  name,
  ...restProps
}) {
  const { t } = useTranslation()
  const [hasOptedIn, setHasOptedIn] = useState(!optinRequired)

  const handleChange = (event) => {
    onChange(event.target.value)
  }

  const selectedIsItem = items
    // casting both values to string, because the values can match in meaning ("1" vs 1),
    // but their types may be different. Resulting in a raw number being displayed as selected,
    // instead of the option it maps to. Items without a value are rendered
    // with their index as value, so match on that same fallback.
    .find(
      (item, index) =>
        String(typeof item.value === 'undefined' ? index : item.value) ===
        String(value),
    )

  // If there is a selected value that is not part of the items array,
  // render it as an extra option without mutating the items prop.
  const menuItems =
    value && !selectedIsItem ? [...items, { label: value, value }] : items

  return (
    <StyledFormControl
      $value={value}
      className={className}
      error={!!error}
      required={required}
      selected={value}
      variant={filled ? 'filled' : undefined}
      data-test-e2e={formatDataTestE2eAttr(id || name)}
      name={name}
      {...restProps}
    >
      <InputLabel htmlFor={id} disabled={disabled}>
        {label}
      </InputLabel>
      <Select
        inputProps={{
          'data-test-e2e': formatDataTestE2eAttr(id || name, 'input'),
          'aria-labelledby': id,
        }}
        id={id}
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled || !hasOptedIn}
        MenuProps={{
          'data-test-e2e': formatDataTestE2eAttr(id || name, 'menu'),
          style: { zIndex: 1320 }, // ensure above MobileActionBarButton (1310)
        }}
      >
        {!selectionRequired && (
          <MenuItem value={null}>--{t('resetSelection')}--</MenuItem>
        )}
        {menuItems.map((item, index) => (
          <MenuItem
            key={index.toString()}
            value={typeof item.value === 'undefined' ? index : item.value}
            disabled={item.disabled || !hasOptedIn}
          >
            {item.label}
          </MenuItem>
        ))}
      </Select>
      {typeof error !== 'string' && optinRequired && !hasOptedIn && (
        <StyledTextLink onClick={() => setHasOptedIn(true)} text={t('edit')} />
      )}
      {typeof error === 'string' && <Error type="ErrorText">{error}</Error>}
    </StyledFormControl>
  )
}

export const propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Render the input with a filled background if truthy */
  filled: PropTypes.bool,
  id: PropTypes.string,
  /** An array of object(s) as input wich populated the option list */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node, // Label can be anything that can be rendered. See: https://legacy.reactjs.org/docs/rendering-elements.html
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool, // consider using radio's or toggles instead.
      ]).isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  onBlur: PropTypes.func,
  /** Supplied onChange handler */
  onChange: PropTypes.func.isRequired,
  /** Placeholder text for floating label */
  label: PropTypes.string, // Was "isRequired" before, but floating label is not always used (e.g. in language-picker.js)
  required: PropTypes.bool,
  /** The currently selected value of the dropdown */
  value: PropTypes.any.isRequired,
  /** Won't render the empty selection option if true */
  selectionRequired: PropTypes.bool,
  optinRequired: PropTypes.bool,
  name: PropTypes.string,
}

NewDropdownSelect.propTypes = propTypes

NewDropdownSelect.defaultProps = {
  className: undefined,
  disabled: false,
  onBlur: undefined,
  error: false,
  id: undefined,
  filled: false,
  required: false,
  selectionRequired: false,
  optinRequired: false,
}

export default NewDropdownSelect
